import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
 TrendingUp, TrendingDown, Users, Clock, Wifi, Smartphone, FileText,
 ArrowRight, Plus, Receipt, MapPin, Activity, Server, Zap, ShieldAlert,
 Calendar, Eye
} from 'lucide-react';
import BranchHealthAlert from '../components/BranchHealthAlert';
import CustomerSegmentCards, { SecondaryKPIRow, VoucherKPIRow } from '../components/CustomerSegmentCards';
import RecentActivityCard from '../components/RecentActivityCard';
import { RadiusLinkBadge } from '../components/RadiusLinkBadge';
import {
 ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, LabelList,
 PieChart, Pie, Cell
} from 'recharts';

// Glassmorphism palette - vibrant but transparent-friendly
const ACCENT = {
 blue: { hex: '#3b82f6', glow: 'rgba(59, 130, 246, 0.25)', light: 'rgba(59, 130, 246, 0.1)' },
 orange: { hex: '#f97316', glow: 'rgba(249, 115, 22, 0.25)', light: 'rgba(249, 115, 22, 0.1)' },
 red: { hex: '#ef4444', glow: 'rgba(239, 68, 68, 0.25)', light: 'rgba(239, 68, 68, 0.1)' },
 yellow: { hex: '#eab308', glow: 'rgba(234, 179, 8, 0.25)', light: 'rgba(234, 179, 8, 0.1)' },
 green: { hex: '#10b981', glow: 'rgba(16, 185, 129, 0.25)', light: 'rgba(16, 185, 129, 0.1)' },
 violet: { hex: '#8b5cf6', glow: 'rgba(139, 92, 246, 0.25)', light: 'rgba(139, 92, 246, 0.1)' },
 pink: { hex: '#ec4899', glow: 'rgba(236, 72, 153, 0.25)', light: 'rgba(236, 72, 153, 0.1)' },
};

// Glass surface - reusable styles
const glassBase = {
 background: 'rgba(255, 255, 255, 0.55)',
 backdropFilter: 'blur(20px) saturate(180%)',
 WebkitBackdropFilter: 'blur(20px) saturate(180%)',
 border: '1px solid rgba(255, 255, 255, 0.6)',
 boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07), inset 0 1px 0 0 rgba(255,255,255,0.7)',
};

const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
const fmtIDRShort = (n) => {
 const v = Number(n || 0);
 if (v >= 1e9) return 'Rp ' + (v / 1e9).toFixed(1) + 'M';
 if (v >= 1e6) return 'Rp ' + (v / 1e6).toFixed(1) + 'jt';
 if (v >= 1e3) return 'Rp ' + (v / 1e3).toFixed(0) + 'rb';
 return 'Rp ' + v;
};

// Glassmorphic KPI card
const KPICard = ({ title, value, icon: Icon, color, subtitle, trend, testid }) => {
 const c = ACCENT[color] || ACCENT.blue;
 const trendUp = trend && trend > 0;
 const trendDown = trend && trend < 0;
 return (
 <div data-testid={testid} className="relative rounded-2xl p-4 transition-all duration-300 group cursor-default overflow-hidden hover:-translate-y-0.5"
 style={glassBase}>
 {/* Floating glow orb (color accent) */}
 <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-40 group-hover:opacity-60 blur-2xl transition-opacity duration-500"
 style={{ background: c.hex }} />
 <div className="relative z-10">
 <div className="flex items-start justify-between mb-3">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-700 leading-tight dark:text-slate-200">{title}</div>
 <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
 style={{
 background: `linear-gradient(135deg, ${c.glow} 0%, ${c.light} 100%)`,
 border: `1px solid ${c.glow}`,
 color: c.hex,
 boxShadow: `0 4px 12px ${c.light}`,
 }}>
 <Icon className="w-4 h-4" />
 </div>
 </div>
 <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-0.5 truncate dark:text-slate-100" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{value}</div>
 <div className="flex items-center justify-between gap-2 mt-1">
 {subtitle && <div className="text-xs text-slate-600 truncate dark:text-slate-300">{subtitle}</div>}
 {(trendUp || trendDown) && (
 <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 backdrop-blur-md flex-shrink-0 ${trendUp ? 'bg-emerald-500/15 text-emerald-700 border border-emerald-500/30' : 'bg-rose-500/15 text-rose-700 border border-rose-500/30'}`}>
 {trendUp ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
 {Math.abs(trend).toFixed(1)}%
 </span>
 )}
 </div>
 </div>
 </div>
 );
};

const QuickAction = ({ icon: Icon, label, to, color = 'blue' }) => {
 const c = ACCENT[color] || ACCENT.blue;
 return (
 <Link to={to} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all group min-w-0 hover:-translate-y-0.5"
 style={{
 background: 'rgba(255, 255, 255, 0.5)',
 backdropFilter: 'blur(12px)',
 WebkitBackdropFilter: 'blur(12px)',
 border: '1px solid rgba(255, 255, 255, 0.7)',
 }}>
 <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
 style={{
 background: `linear-gradient(135deg, ${c.glow} 0%, ${c.light} 100%)`,
 color: c.hex,
 }}>
 <Icon className="w-4 h-4" />
 </div>
 <span className="text-xs font-semibold text-slate-800 group-hover:text-slate-900 truncate transition-colors dark:text-slate-200">{label}</span>
 </Link>
 );
};

const SectionCard = ({ title, icon: Icon, action, children, accent = 'blue' }) => {
 const c = ACCENT[accent] || ACCENT.blue;
 return (
 <div className="rounded-2xl overflow-hidden" style={glassBase}>
 <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.15)' }}>
 <div className="flex items-center gap-2.5">
 {Icon && (
 <div className="w-7 h-7 rounded-lg flex items-center justify-center"
 style={{
 background: `linear-gradient(135deg, ${c.glow} 0%, ${c.light} 100%)`,
 color: c.hex,
 }}>
 <Icon className="w-4 h-4" />
 </div>
 )}
 <h3 className="font-semibold text-slate-800 text-sm dark:text-slate-200">{title}</h3>
 </div>
 {action}
 </div>
 <div className="p-4">{children}</div>
 </div>
 );
};

const DashboardV2 = ({
 stats,
 routers,
 packageStats,
 registrations,
 revenueTrend,
 recentCustomers,
 serviceInfo,
 loading,
 selectedBranch,
}) => {
 const { user: _authUserHF } = useAuth();
 const _isTeknisi = (_authUserHF?.role || '').toLowerCase() === 'teknisi';
  // 2026-07-11: Informasi Layanan (subscription info) only for tenant admin roles
  const _isTenantAdmin = ['super_admin', 'superadmin', 'admin'].includes((_authUserHF?.role || '').toLowerCase());

 const incomeToday = stats?.income_today || 0;
 const incomeYesterday = stats?.income_yesterday || 0;
 const incomeChange = incomeYesterday > 0 ? ((incomeToday - incomeYesterday) / incomeYesterday) * 100 : 0;

 const totalCustomers = stats?.total_customers || 0;
 const activePppoe = stats?.pppoe_online || 0;
 const activeHotspot = stats?.hotspot_online || 0;
 const isolated = stats?.isolated_customers || 0;
 const pendingInvoices = stats?.pending_invoices || 0;
 const dueCustomers = stats?.due_customers || 0;

 const onlineRouters = routers.filter(r => r.status === 'online' || r.is_online).length;
 const totalRouters = routers.length;

 const PIE_COLORS = [ACCENT.blue.hex, ACCENT.orange.hex, ACCENT.violet.hex, ACCENT.yellow.hex, ACCENT.green.hex, ACCENT.red.hex, ACCENT.pink.hex];

 const RegTooltip = ({ active, payload }) => {
 if (!active || !payload || !payload[0]) return null;
 const p = payload[0].payload || {};
 const d = p.date || '';
 let label = d;
 try {
 const dt = new Date(d);
 if (!isNaN(dt)) label = dt.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
 } catch {}
 const count = p.count || 0;
 return (
 <div style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 10, padding: '8px 12px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
 <div style={{ color: '#64748b', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
 <div style={{ color: '#0f172a', fontWeight: 700 }}>
 <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981', marginRight: 6 }} />
 {count} Registrasi
 </div>
 </div>
 );
 };
 // Custom dot: larger green circle for non-zero, small invisible for zero (cleaner look)
 const RegDot = (props) => {
 const { cx, cy, payload } = props;
 const v = payload?.count || 0;
 if (v === 0) return <circle cx={cx} cy={cy} r={2} fill={'#10b981'} fillOpacity={0.5} />;
 return <circle cx={cx} cy={cy} r={5} fill={'#10b981'} stroke={'#fff'} strokeWidth={2} />;
 };


 if (loading) {
 return (
 <div className="flex items-center justify-center min-h-[400px]"
 style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #fce7f3 50%, #ddd6fe 100%)' }}>
 <div className="animate-spin rounded-full h-12 w-12 border-2" style={{ borderColor: ACCENT.violet.hex, borderTopColor: 'transparent' }}></div>
 </div>
 );
 }

 return (
 <div className="space-y-4 p-3 sm:p-5 lg:p-6 pt-2 sm:pt-3 lg:pt-3 min-h-screen relative overflow-hidden"
 style={{
 background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 25%, #f5f3ff 50%, #ede9fe 75%, #f0f9ff 100%)',
 }}>
 {/* Animated background blobs - macOS Big Sur style */}
 <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-15 blur-3xl pointer-events-none animate-pulse" style={{ background: ACCENT.blue.hex, animationDuration: '8s' }} />
 <div className="absolute top-1/3 -right-32 w-[28rem] h-[28rem] rounded-full opacity-10 blur-3xl pointer-events-none animate-pulse" style={{ background: '#22d3ee', animationDuration: '12s', animationDelay: '2s' }} />
 <div className="absolute bottom-0 left-1/3 w-96 h-96 rounded-full opacity-12 blur-3xl pointer-events-none animate-pulse" style={{ background: ACCENT.violet.hex, animationDuration: '10s', animationDelay: '4s' }} />

 <div className="relative z-10 space-y-4">
 <BranchHealthAlert selectedBranch={selectedBranch} variant="light" />
 {/* HERO INCOME CARD - Clean white surface */}
{!_isTeknisi && (
<div className="relative">
<RadiusLinkBadge />
<div className="relative rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
<div className="relative p-4 sm:p-5 lg:p-6">
 <div>
 <div className="flex items-center gap-2 mb-2">
 <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-50 ring-1 ring-amber-100 dark:bg-amber-900/30">
 <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
 </div>
 <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider dark:text-slate-300">Pendapatan Hari Ini</span>
 </div>
 <div className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-3 tracking-tight" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', color: '#334155' }} data-testid="hero-income-today">
 {fmtIDR(incomeToday)}
 </div>
 <div className="flex items-center gap-3 flex-wrap">
 {incomeChange !== 0 && (
 <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 backdrop-blur-md ${incomeChange > 0 ? 'bg-emerald-500/20 text-emerald-700 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-700 border border-rose-500/30'}`}>
 {incomeChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
 {Math.abs(incomeChange).toFixed(1)}% vs kemarin
 </span>
 )}
 <span className="text-xs text-slate-600 dark:text-slate-300">
 Kemarin: <span className="text-slate-800 font-medium dark:text-slate-200">{fmtIDR(incomeYesterday)}</span>
 </span>
 </div>
 </div>
 </div>
 </div>
 </div>

 )}

 {/* CUSTOMER SEGMENT CARDS — TOTAL / ONLINE / OFFLINE */}
 <VoucherKPIRow stats={stats} />
 <CustomerSegmentCards stats={stats} variant='glass' />

 {/* SECONDARY KPI ROW — Belum Bayar / Jatuh Tempo / Diisolir */}
 <SecondaryKPIRow pendingInvoices={pendingInvoices} dueCustomers={dueCustomers} isolated={isolated} variant="glass" />

 {/* MAIN GRID */}
 <div className={`grid grid-cols-1 ${(_isTeknisi || !_isTenantAdmin) ? "lg:grid-cols-2" : "lg:grid-cols-3"} gap-4`}>
 {_isTenantAdmin && <SectionCard title="Informasi Layanan" icon={Activity} accent="blue">
 <div className="space-y-1.5">
 {[
 { label: 'Paket Subscription', value: serviceInfo?.subscription?.plan_name || stats?.subscription_plan || 'ENTERPRISE', sub: `Max ${(serviceInfo?.subscription?.max_customers ?? stats?.max_users ?? 5000).toLocaleString('id-ID')} users`, color: 'blue', icon: Server },
 { label: 'PPPoE Users', value: `${(serviceInfo?.usage?.pppoe_used ?? stats?.total_pppoe_customers ?? 0).toLocaleString('id-ID')}`, sub: `dari ${(serviceInfo?.subscription?.max_customers ?? 5000).toLocaleString('id-ID')}`, color: 'green', icon: Wifi },
 { label: 'Hotspot Users', value: `${(serviceInfo?.usage?.hotspot_used ?? activeHotspot ?? 0).toLocaleString('id-ID')}`, sub: `dari ${(serviceInfo?.subscription?.max_hotspot ?? 5000).toLocaleString('id-ID')}`, color: 'violet', icon: Smartphone },
 { label: 'MikroTik Devices', value: `${serviceInfo?.mikrotik?.used ?? onlineRouters} / ${serviceInfo?.mikrotik?.max ?? totalRouters ?? 20}`, sub: `Tersedia: ${serviceInfo?.mikrotik?.available ?? ((totalRouters || 20) - onlineRouters)}`, color: 'red', icon: Server },
 { label: 'Jatuh Tempo Subs', value: serviceInfo?.subscription?.subscription_end_date ? new Date(serviceInfo.subscription.subscription_end_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : (stats?.subscription_due_date || '-'), sub: serviceInfo?.subscription?.days_until_expiry != null ? `${serviceInfo.subscription.days_until_expiry} hari lagi` : (stats?.subscription_due_days != null ? `${stats.subscription_due_days} Hari` : '-'), color: 'orange', icon: Calendar },
 ].map((row, i) => {
 const rc = ACCENT[row.color] || ACCENT.blue;
 const RowIcon = row.icon;
 return (
 <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl transition-all hover:bg-white/40">
 <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
 style={{
 background: `linear-gradient(135deg, ${rc.glow} 0%, ${rc.light} 100%)`,
 color: rc.hex,
 }}>
 <RowIcon className="w-4 h-4" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="text-xs text-slate-700 font-medium dark:text-slate-200">{row.label}</div>
 <div className="text-[11px] text-slate-500 dark:text-slate-400">{row.sub}</div>
 </div>
 <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{ fontVariantNumeric: 'tabular-nums' }}>{row.value}</div>
 </div>
 );
 })}
 </div>
 </SectionCard>}

{!_isTeknisi && (
 <SectionCard title="Tren Pendapatan Tahun Ini" icon={TrendingUp} accent="blue"
 action={<Link to="/app/revenue" className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors dark:text-blue-400">Detail <ArrowRight className="w-3 h-3" /></Link>}>
 {revenueTrend.length > 0 ? (
 <div className="h-56">
 <ResponsiveContainer width="100%" height="100%">
 <AreaChart data={revenueTrend} margin={{ top: 18, right: 16, left: -8, bottom: 4 }}>
 <defs>
 <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
 <stop offset="0%" stopColor={ACCENT.blue.hex} stopOpacity={0.28} />
 <stop offset="100%" stopColor={ACCENT.blue.hex} stopOpacity={0} />
 </linearGradient>
 </defs>
 <CartesianGrid stroke="rgba(148,163,184,0.18)" strokeDasharray="4 6" vertical={false} />
 <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8', fontWeight: 500 }} axisLine={false} tickLine={false} dy={6} />
 <YAxis
 domain={[(dataMin) => Math.max(0, Math.floor(dataMin * 0.97)), (dataMax) => Math.ceil(dataMax * 1.03)]}
 tickFormatter={fmtIDRShort}
 tick={{ fontSize: 10, fill: '#94a3b8' }}
 axisLine={false}
 tickLine={false}
 width={48}
 />
 <Tooltip
 formatter={(v) => fmtIDR(v)}
 cursor={{ stroke: ACCENT.blue.hex, strokeWidth: 1, strokeDasharray: '4 4', opacity: 0.4 }}
 contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 12, fontWeight: 500, color: '#0f172a', boxShadow: '0 8px 24px rgba(15,23,42,0.08)' }}
 labelStyle={{ color: '#64748b', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
 />
 <Area
 type="monotone"
 dataKey="amount"
 stroke={ACCENT.blue.hex}
 strokeWidth={2.5}
 fill="url(#revFill)"
 dot={{ fill: '#ffffff', stroke: ACCENT.blue.hex, strokeWidth: 2, r: 3.5 }}
 activeDot={{ fill: ACCENT.blue.hex, stroke: '#ffffff', strokeWidth: 2.5, r: 6 }}
 />
 </AreaChart>
 </ResponsiveContainer>
 </div>
 ) : (
 <div className="h-56 flex items-center justify-center text-slate-400 text-sm dark:text-slate-500">Belum ada data pendapatan</div>
 )}
 </SectionCard>
 )}

 <SectionCard title="Status Router MikroTik" icon={Server} accent="green"
 action={<span className="text-[10px] font-bold text-emerald-700 bg-emerald-500/15 backdrop-blur-md border border-emerald-500/30 px-2 py-0.5 rounded-full dark:text-emerald-300">{onlineRouters}/{totalRouters} Online</span>}>
 <div className="space-y-2 max-h-[460px] overflow-y-auto scrollbar-hide pr-1" data-testid="router-status-scrollable">
 {routers.length === 0 ? (
 <div className="text-center py-6 text-slate-400 text-sm dark:text-slate-500">Belum ada router terdaftar</div>
 ) : routers.map((r, i) => {
 const isOnline = r.status === 'online' || r.is_online;
 return (
 <div key={r.id || i} className="flex items-center gap-3 p-2.5 rounded-xl transition-all hover:bg-white/40"
 style={{ background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.5)' }}>
 <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}
 style={isOnline ? { boxShadow: `0 0 8px ${ACCENT.green.hex}` } : {}}></div>
 <Wifi className={`w-4 h-4 ${isOnline ? 'text-emerald-600' : 'text-slate-400'}`} />
 <div className="flex-1 min-w-0">
 <div className="text-sm font-semibold text-slate-800 truncate dark:text-slate-200">{r.name || r.host || 'Router'}</div>
 <div className="text-xs text-slate-500 truncate dark:text-slate-400">{r.host || r.ip_address} {r.location ? `• ${r.location}` : ''}</div>
 </div>
 {typeof r.active_sessions === 'number' && (
 <span
 className="relative inline-flex items-center justify-center min-w-[52px] h-6 px-2 rounded-md bg-white/70 dark:bg-slate-800/70 border border-slate-300/60 dark:border-slate-600/40 mr-1 overflow-hidden"
 title={r.active_error || `${r.active_sessions} active PPPoE sessions`}
 data-testid={`router-active-${r.id || i}`}
 >
 <span className="relative z-10 text-xs font-bold text-slate-800 dark:text-white tabular-nums">
 {(r.active_sessions || 0).toLocaleString('id-ID')}
 </span>
 </span>
 )}
 <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-md ${isOnline ? 'bg-emerald-500/15 text-emerald-700 border border-emerald-500/30' : 'bg-slate-500/10 text-slate-500'}`}>
 {isOnline ? 'Online' : 'Offline'}
 </span>
 </div>
 );
 })}
 
 </div>
 </SectionCard>
 </div>

 {/* SECONDARY GRID */}
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
 <SectionCard title="Distribusi Paket Pelanggan" icon={Activity} accent="violet">
 {packageStats.length > 0 ? (
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div className="h-48">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie data={packageStats} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
 {packageStats.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="rgba(255,255,255,0.6)" strokeWidth={2} />)}
 </Pie>
 <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 12, fontSize: 11 }} />
 </PieChart>
 </ResponsiveContainer>
 </div>
 <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
 {packageStats.map((p, i) => (
 <div key={i} className="flex items-center gap-2 text-xs">
 <div className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
 <span className="flex-1 text-slate-700 truncate dark:text-slate-200">{p.name}</span>
 <span className="font-bold text-slate-900 dark:text-slate-100" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.count}</span>
 </div>
 ))}
 </div>
 </div>
 ) : (
 <div className="h-48 flex items-center justify-center text-slate-400 text-sm dark:text-slate-500">Belum ada data paket</div>
 )}
 </SectionCard>

 <SectionCard title="Aktivitas Registrasi (30 Hari)" icon={Calendar} accent="green">
 {registrations.length > 0 ? (
 <div className="h-48">
 <ResponsiveContainer width="100%" height="100%">
 <LineChart data={registrations.slice(-30)} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" vertical={false} />
 <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} interval={4} tickFormatter={(d) => (d || '').substring(8) + '/' + (d || '').substring(5,7)} />
 <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
 <Tooltip content={<RegTooltip />} cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '3 3' }} />
 <Line type="monotone" dataKey="count" stroke={ACCENT.green.hex} strokeWidth={2.5} dot={<RegDot />} activeDot={{ r: 7, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }} />
 </LineChart>
 </ResponsiveContainer>
 </div>
 ) : (
 <div className="h-48 flex items-center justify-center text-slate-400 text-sm dark:text-slate-500">Belum ada registrasi</div>
 )}
 </SectionCard>

 <RecentActivityCard variant="glass" />

 <SectionCard title="Pelanggan Terbaru" icon={Users} accent="orange"
 action={<Link to="/app/customers" className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1 transition-colors dark:text-orange-400">Lihat Semua <ArrowRight className="w-3 h-3" /></Link>}>
 <div className="space-y-1.5">
 {recentCustomers.length === 0 ? (
 <div className="text-center py-6 text-slate-400 text-sm dark:text-slate-500">Belum ada pelanggan</div>
 ) : recentCustomers.slice(0, 6).map(c => (
 <Link key={c.id} to={`/app/customers/${c.id}`} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/40 transition-colors">
 <div className="w-8 h-8 rounded-full text-white flex items-center justify-center font-bold text-sm flex-shrink-0"
 style={{ background: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)', boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)' }}>
 {(c.name || '?').charAt(0).toUpperCase()}
 </div>
 <div className="flex-1 min-w-0">
 <div className="text-sm font-semibold text-slate-800 truncate dark:text-slate-200">{c.name}</div>
 <div className="text-xs text-slate-500 truncate dark:text-slate-400">{c.service_plan_name || c.pppoe_username || '-'}</div>
 </div>
 <Eye className="w-4 h-4 text-slate-400 dark:text-slate-500" />
 </Link>
 ))}
 </div>
 </SectionCard>
 </div>
 </div>
 </div>
 );
};

export default DashboardV2;
